import { makeStyles } from "tss-react/mui"
import { Stack } from "@mui/material"
import { Form, Formik } from "formik"
import * as Yup from "yup"
import { DateTimePicker } from "@mui/x-date-pickers"
import { DateTime } from "luxon"
import { StandardDialog } from "../../../../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../../localization/components/AppLocalizationProvider"
import { FormikSubmitButton, FormikTextField } from "../../../../../../../../shared/components/forms/Formik"
import { MAX_ORDER_COMMENT_LENGTH } from "../../../../../../../../constants/inputValidationDefaults"

const useStyles = makeStyles()(() => ({
    container: {
        padding: "2rem 4rem",
    },
}))

type AddClosingTimeDialogProps = Readonly<{
    onAdd: ({ start, end, reason }: { start?: DateTime; end?: DateTime; reason: string }) => void
    open: boolean
    onClose: () => void
}>

type ClosingTimeFormValues = {
    start: DateTime
    end: DateTime
    reason: string
}

export function AddClosingTimeDialog({ open, onClose, onAdd }: AddClosingTimeDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const initialValues: ClosingTimeFormValues = {
        start: DateTime.now().minus({ days: 1 }),
        end: DateTime.now(),
        reason: "",
    }

    const validationSchema = Yup.object({
        reason: Yup.string().max(
            MAX_ORDER_COMMENT_LENGTH,
            l10n.getString("tab-availability-modal-reason-max", { max: MAX_ORDER_COMMENT_LENGTH }),
        ),
    })

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="tab-availability-modal-title">
                    <>Add closed period</>
                </LocalizedStrict>
            }
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            scroll="body"
        >
            <Formik<ClosingTimeFormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => onAdd({ start: values.start, end: values.end, reason: values.reason })}
            >
                {({ values, setFieldValue }) => (
                    <Form>
                        <Stack className={classes.container} spacing="2rem">
                            <Stack direction="row" spacing="1rem">
                                <LocalizedStrict id="tab-availability-side-start-time-label" attrs={{ label: true }}>
                                    <DateTimePicker
                                        value={values.start}
                                        onChange={(newValue) => {
                                            if (newValue === null) return
                                            setFieldValue("start", newValue)
                                        }}
                                        ampm={false}
                                        label="Start"
                                    />
                                </LocalizedStrict>
                                <LocalizedStrict id="tab-availability-side-end-time-label" attrs={{ label: true }}>
                                    <DateTimePicker
                                        value={values.end}
                                        onChange={(newValue) => {
                                            if (newValue === null) return
                                            setFieldValue("end", newValue)
                                        }}
                                        ampm={false}
                                        label="End"
                                    />
                                </LocalizedStrict>
                            </Stack>
                            <LocalizedStrict id="tab-availability-modal-reason-label" attrs={{ label: true }}>
                                <FormikTextField<ClosingTimeFormValues> name="reason" label="Reason" />
                            </LocalizedStrict>
                            <Stack direction="row" justifyContent="center">
                                <LocalizedStrict id="tab-availability-modal-add-button">
                                    <FormikSubmitButton fullWidth={false}>Add</FormikSubmitButton>
                                </LocalizedStrict>
                            </Stack>
                        </Stack>
                    </Form>
                )}
            </Formik>
        </StandardDialog>
    )
}
